import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, Upload, FileImage } from 'lucide-react';
import { toast } from 'sonner';

const PaymentModal = ({ isOpen, onClose, cartItems, cartAssets, totalAmount, onPaymentSubmit }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);

  if (!isOpen) return null;

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type (images only)
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file only');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      
      setUploadedFile(file);
      toast.success('Transaction screenshot uploaded successfully');
    }
  };

  const handleSubmitForReview = () => {
    if (!uploadedFile) {
      toast.error('Please upload your transaction screenshot first');
      return;
    }
    
    if (!agreementChecked) {
      toast.error('Please agree to the terms and conditions to proceed');
      return;
    }
    
    setShowSuccessModal(true);
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onClose();
    onPaymentSubmit();
  };

  const SuccessModal = () => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 sm:p-8 text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        
        <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-gray-900">
          Thank You for Your Booking!
        </h2>
        
        <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 leading-relaxed">
          Your reservation request and transaction screenshot have been successfully submitted for review. 
          Our team will verify your payment and complete the due diligence process within 3-5 business days.
        </p>
        
        <p className="text-sm sm:text-base text-gray-500 mb-6 sm:mb-8">
          Upon successful confirmation, your selected assets will appear in the <strong>'My Assets'</strong> section 
          with detailed status updates on the next steps in your fractional ownership journey.
        </p>
        
        <Button
          onClick={handleSuccessClose}
          className="w-full bg-black text-white hover:bg-black/90 py-3 sm:py-4 text-base sm:text-lg font-semibold"
        >
          I Understand
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Complete Your Reservation</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex flex-col lg:flex-row max-h-[calc(95vh-100px)]">
            {/* Left Section - Payment Process & T&C */}
            <div className="w-full lg:w-1/2 p-4 sm:p-6 lg:border-r border-gray-200 overflow-y-auto">
              <h3 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-900">Payment Process & Terms</h3>
              
              <div className="space-y-4 sm:space-y-6 text-sm sm:text-base text-gray-600 leading-relaxed">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                  <h4 className="font-semibold text-amber-800 mb-2 text-sm sm:text-base">Current Stage: Booking Confirmation</h4>
                  <p className="text-amber-700 text-sm sm:text-base">
                    You are currently completing <strong>Stage 1</strong> of the fractional ownership process.
                  </p>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  <div className="border-l-4 border-blue-500 pl-3 sm:pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Stage 1: Ticket Blocking Amount</h4>
                    <p className="text-sm sm:text-base">
                      <strong>Amount:</strong> ₹10,000 per ticket<br/>
                      <strong>Purpose:</strong> Secures your position in the SPV group<br/>
                      <strong>Process:</strong> Upon payment confirmation, our team will begin due diligence and member curation to form your ownership group.
                    </p>
                  </div>

                  <div className="border-l-4 border-green-500 pl-3 sm:pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Stage 2: Full Ticket Value Investment</h4>
                    <p className="text-sm sm:text-base">
                      <strong>Timeline:</strong> Maximum 30 days after Stage 1 completion<br/>
                      <strong>Amount:</strong> Complete Ticket amount + Year 1 Annual Platform Fees (For Individual Vehicles)<br/>
                      <strong>Process:</strong> Final payment to secure your fractional ownership stake.
                    </p>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-3 sm:pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Stage 3: Year 2 Platform Fees</h4>
                    <p className="text-sm sm:text-base">
                      <strong>Timeline:</strong> 12 months after asset deployment<br/>
                      <strong>Amount:</strong> Year 2 Annual Platform Fees (For Individual Vehicles)<br/>
                      <strong>Purpose:</strong> Continued platform services and asset management.
                    </p>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-3 sm:pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Stage 4: Year 3 Platform Fees</h4>
                    <p className="text-sm sm:text-base">
                      <strong>Timeline:</strong> 24 months after asset deployment<br/>
                      <strong>Amount:</strong> Year 3 Annual Platform Fees (For Individual Vehicles)<br/>
                      <strong>Purpose:</strong> Final year of asset management services.
                    </p>
                  </div>

                  <div className="border-l-4 border-indigo-500 pl-3 sm:pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Stage 5: Residual Value Return</h4>
                    <p className="text-sm sm:text-base">
                      <strong>Timeline:</strong> Upon End of 3-year ownership period<br/>
                      <strong>Process:</strong> Asset liquidation and proportional refund of residual value<br/>
                      <strong>Note:</strong> Residual amount subject to market conditions and initial agreement terms.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Important Terms & Conditions</h4>
                  <ul className="list-disc pl-4 sm:pl-5 space-y-1 text-xs sm:text-sm">
                    <li>All payments are non-refundable except for the residual value return in Stage 5</li>
                    <li>Platform fees are subject to annual review and may be adjusted</li>
                    <li>Asset usage rights are allocated based on your ownership percentage</li>
                    <li>All payments must be completed within specified timelines to maintain ownership rights</li>
                    <li>The Haute Circle reserves the right to modify terms with 30 days notice</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Section - Payment */}
            <div className="w-full lg:w-1/2 p-4 sm:p-6 overflow-y-auto">
              <h3 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-900">Booking Summary</h3>
              
              {/* Cart Items Summary */}
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => {
                  const asset = cartAssets[item.asset_id];
                  // Get the correct price per ticket from either the item or the asset
                  const pricePerTicket = item.price_per_ticket || asset?.price_per_ticket || 0;
                  const totalTicketValue = item.tickets * pricePerTicket;
                  
                  return (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold text-base sm:text-lg">{asset?.name || 'Loading...'}</h4>
                          <p className="text-sm text-gray-600">{item.tickets} ticket(s)</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-gold">₹{totalTicketValue.toLocaleString()}</p>
                          <p className="text-sm text-gray-600">Total Value</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-sm text-gray-600">₹{pricePerTicket.toLocaleString()}/ticket</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total Amount */}
              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex justify-between items-center text-lg sm:text-xl font-bold">
                  <span>Stage 1 Booking Amount:</span>
                  <span className="text-gold">₹{(cartItems.reduce((sum, item) => sum + (item.tickets * 10000), 0)).toLocaleString()}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  (₹10,000 × {cartItems.reduce((sum, item) => sum + item.tickets, 0)} tickets)
                </p>
              </div>

              {/* Payment Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">Payment Instructions</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><strong>Bank Account:</strong> The Haute Circle</p>
                  <p><strong>Account No:</strong> 1234567890</p>
                  <p><strong>IFSC Code:</strong> HDFC0001234</p>
                  <p><strong>UPI ID:</strong> hautecircle@hdfc</p>
                </div>
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-2">
                  Upload Transaction Screenshot *
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-sm sm:text-base text-gray-600">
                      {uploadedFile ? (
                        <span className="flex items-center justify-center gap-2 text-green-600">
                          <FileImage size={16} />
                          {uploadedFile.name}
                        </span>
                      ) : (
                        "Click to upload or drag and drop"
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                  </label>
                </div>
              </div>

              {/* Terms Agreement Checkbox */}
              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreementChecked}
                    onChange={(e) => setAgreementChecked(e.target.checked)}
                    className="mt-1 h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
                  />
                  <span className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    I acknowledge that I have read, understood, and agree to the payment process, terms and conditions, 
                    and stage-wise payment structure outlined above for the ticket blocking amount and fractional ownership program.
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmitForReview}
                disabled={!uploadedFile || !agreementChecked}
                className="w-full bg-black text-white hover:bg-black/90 py-3 sm:py-4 text-base sm:text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit for Review
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && <SuccessModal />}
    </>
  );
};

export default PaymentModal;